// frontend/src/components/admin/WhatsAppBotPanel.jsx
import React, { useState, useEffect } from 'react';
import api from '../../config/api';
import { 
    MessageCircle, 
    CheckCircle, 
    XCircle, 
    Loader2, 
    RefreshCw, 
    Send, 
    Phone,
    AlertCircle,
    WifiOff
} from 'lucide-react';
import Swal from 'sweetalert2';

export const WhatsAppBotPanel = () => {
    const [status, setStatus] = useState('disconnected');
    const [qrImage, setQrImage] = useState(null);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [phoneNumber, setPhoneNumber] = useState(null);
    const [sending, setSending] = useState(false);
    const [testPhone, setTestPhone] = useState('');
    const [testMessage, setTestMessage] = useState('');

    useEffect(() => {
        loadData();
        // Actualizar cada 30 segundos
        const interval = setInterval(() => {
            loadStatus();
        }, 30000);
        
        return () => clearInterval(interval);
    }, []);

    const loadData = async () => {
        await Promise.all([
            loadStatus(),
            loadQR()
        ]);
    };

    const loadStatus = async () => {
        try {
            const response = await api.get('/admin/whatsapp/status');
            setStatus(response.data.status || 'disconnected');
            setPhoneNumber(response.data.number || null);
        } catch (error) {
            console.error('Error loading status:', error);
            setStatus('error');
        }
    };

    const loadQR = async () => {
        try {
            setLoading(true);
            const response = await api.get('/admin/whatsapp/qr', {
                responseType: 'blob'
            });
            
            if (response.status === 200) {
                const url = URL.createObjectURL(response.data);
                setQrImage(url);
            } else {
                setQrImage(null);
            }
        } catch (error) {
            console.error('Error loading QR:', error);
            setQrImage(null);
        } finally {
            setLoading(false);
        }
    };

    const refreshQR = async () => {
        setRefreshing(true);
        await loadQR();
        setRefreshing(false);
    };

    const sendTestMessage = async (e) => {
        e.preventDefault();
        
        if (!testPhone || !testMessage) {
            Swal.fire({
                icon: 'warning',
                title: 'Campos obrigatórios',
                text: 'Preencha o telefone e a mensagem.',
                confirmButtonColor: '#449879'
            });
            return;
        }

        setSending(true);
        try {
            const response = await api.post('/admin/whatsapp/send-test', {
                to: testPhone,
                message: testMessage
            });

            if (response.data.success) {
                Swal.fire({
                    icon: 'success',
                    title: '✅ Mensagem enviada!',
                    text: `Mensagem enviada para ${testPhone}`,
                    timer: 3000,
                    showConfirmButton: false,
                    toast: true,
                    position: 'top-end'
                });
                setTestPhone('');
                setTestMessage('');
            } else {
                throw new Error(response.data.error || 'Erro ao enviar');
            }
        } catch (error) {
            Swal.fire({
                icon: 'error',
                title: '❌ Erro ao enviar',
                text: error.response?.data?.error || error.message || 'Não foi possível enviar a mensagem.',
                confirmButtonColor: '#449879'
            });
        } finally {
            setSending(false);
        }
    };

    const sendTestToUser = async () => {
        const { value: userId } = await Swal.fire({
            title: 'Enviar teste para usuário',
            text: 'Digite o ID do usuário que receberá a mensagem de teste:',
            input: 'number',
            inputPlaceholder: 'ID do usuário (ex: 1)',
            showCancelButton: true,
            confirmButtonColor: '#449879',
            cancelButtonColor: '#6b7280',
            confirmButtonText: 'Enviar',
            cancelButtonText: 'Cancelar',
            inputValidator: (value) => {
                if (!value) {
                    return 'Por favor, informe o ID do usuário';
                }
                if (value < 1) {
                    return 'ID inválido';
                }
                return null;
            }
        });

        if (!userId) return;

        setSending(true);
        try {
            const response = await api.post('/admin/whatsapp/send-test-user', {
                user_id: userId
            });

            if (response.data.success) {
                Swal.fire({
                    icon: 'success',
                    title: '✅ Mensagem enviada!',
                    text: `Mensagem de teste enviada para o usuário ID ${userId}`,
                    timer: 3000,
                    showConfirmButton: false,
                    toast: true,
                    position: 'top-end'
                });
            } else {
                throw new Error(response.data.error || 'Erro ao enviar');
            }
        } catch (error) {
            Swal.fire({
                icon: 'error',
                title: '❌ Erro ao enviar',
                text: error.response?.data?.error || error.message || 'Não foi possível enviar a mensagem.',
                confirmButtonColor: '#449879'
            });
        } finally {
            setSending(false);
        }
    };

    const getStatusConfig = () => {
        const configs = {
            connected: {
                label: 'Conectado',
                icon: CheckCircle,
                color: 'text-green-600 bg-green-50 border-green-200',
                dotColor: 'bg-green-500'
            },
            disconnected: {
                label: 'Desconectado',
                icon: XCircle,
                color: 'text-red-600 bg-red-50 border-red-200',
                dotColor: 'bg-red-500'
            },
            waiting_for_scan: {
                label: 'Aguardando QR',
                icon: Loader2,
                color: 'text-yellow-600 bg-yellow-50 border-yellow-200',
                dotColor: 'bg-yellow-500'
            },
            error: {
                label: 'Erro',
                icon: XCircle,
                color: 'text-red-600 bg-red-50 border-red-200',
                dotColor: 'bg-red-500'
            },
            offline: {
                label: 'Offline',
                icon: WifiOff,
                color: 'text-gray-500 bg-gray-50 border-gray-200',
                dotColor: 'bg-gray-400'
            }
        };

        return configs[status] || configs.disconnected;
    };

    const statusConfig = getStatusConfig();
    const StatusIcon = statusConfig.icon;
    const isConnected = status === 'connected';

    return (
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
            {/* Header */}
            <div className="px-6 py-5 border-b border-gray-100 bg-gray-50/50">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <div className="p-2.5 bg-[#25D366]/10 rounded-xl">
                            <MessageCircle className="w-6 h-6 text-[#25D366]" />
                        </div>
                        <div>
                            <h2 className="text-lg font-semibold text-gray-800">WhatsApp Bot</h2>
                            <p className="text-sm text-gray-500">
                                Conecte o WhatsApp Business para enviar notificações automáticas
                            </p>
                        </div>
                    </div>
                    <button
                        onClick={refreshQR}
                        disabled={refreshing}
                        className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                        title="Atualizar QR Code"
                    >
                        <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
                    </button>
                </div>
            </div>

            <div className="p-6">
                {/* Status */}
                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl mb-6">
                    <div className="flex items-center gap-3">
                        <div className={`w-2.5 h-2.5 rounded-full ${statusConfig.dotColor} animate-pulse`} />
                        <span className="text-sm font-medium text-gray-700">Status:</span>
                        <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium border ${statusConfig.color}`}>
                            <StatusIcon className={`w-3.5 h-3.5 ${status === 'waiting_for_scan' ? 'animate-spin' : ''}`} />
                            {statusConfig.label}
                        </span>
                    </div>
                    {phoneNumber && (
                        <span className="text-sm text-gray-500 flex items-center gap-1.5">
                            <Phone className="w-3.5 h-3.5" />
                            {phoneNumber}
                        </span>
                    )}
                </div>

                {/* QR Code */}
                <div className="flex flex-col items-center justify-center py-4">
                    {loading ? (
                        <div className="flex items-center gap-2 text-gray-500 py-8">
                            <Loader2 className="w-5 h-5 animate-spin" />
                            <span>Carregando QR Code...</span>
                        </div>
                    ) : qrImage ? (
                        <>
                            <div className="bg-white p-4 rounded-2xl border-2 border-gray-200 shadow-lg">
                                <img
                                    src={qrImage}
                                    alt="QR Code WhatsApp"
                                    className="w-56 h-56"
                                />
                            </div>
                            <div className="mt-5 text-center max-w-md">
                                <h3 className="text-sm font-semibold text-gray-800 flex items-center justify-center gap-2">
                                    📱 Escaneie com o WhatsApp Business
                                </h3>
                                <ol className="text-xs text-gray-500 mt-3 space-y-1.5 text-left">
                                    <li>1. Abra o <strong>WhatsApp Business</strong> no seu telefone</li>
                                    <li>2. Toque nos <strong>3 pontos</strong> (⋮) → "Dispositivos vinculados"</li>
                                    <li>3. Toque em <strong>"Vincular dispositivo"</strong></li>
                                    <li>4. Aponte a câmera para o <strong>QR Code</strong> acima</li>
                                </ol>
                                <p className="text-xs text-gray-400 mt-3">
                                    ⚠️ O QR Code expira em 2 minutos. Atualize se necessário.
                                </p>
                            </div>
                        </>
                    ) : (
                        <div className="text-center py-8">
                            <div className="w-20 h-20 bg-gray-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                                <WifiOff className="w-10 h-10 text-gray-300" />
                            </div>
                            <p className="text-gray-600 font-medium">QR Code não disponível</p>
                            <p className="text-sm text-gray-400 mt-1">
                                {status === 'connected' 
                                    ? 'O bot já está conectado.' 
                                    : 'Inicie o bot no servidor para gerar o QR Code.'}
                            </p>
                            {status === 'connected' && (
                                <p className="text-xs text-green-600 mt-2 flex items-center justify-center gap-1">
                                    <CheckCircle className="w-3.5 h-3.5" />
                                    Conectado com sucesso!
                                </p>
                            )}
                        </div>
                    )}
                </div>

                {/* Enviar mensagem de teste */}
                <div className="mt-6 pt-6 border-t border-gray-100">
                    <div className="flex items-center justify-between mb-4">
                        <h3 className="text-sm font-medium text-gray-700 flex items-center gap-2">
                            <Send className="w-4 h-4 text-[#449879]" />
                            Enviar mensagem de teste
                        </h3>
                        <button
                            onClick={sendTestToUser}
                            disabled={sending || !isConnected}
                            className={`text-xs font-medium px-3 py-1.5 rounded-lg transition-colors ${
                                isConnected
                                    ? 'text-[#449879] hover:bg-[#449879]/10'
                                    : 'text-gray-400 cursor-not-allowed'
                            }`}
                        >
                            Enviar para usuário
                        </button>
                    </div>
                    
                    <form onSubmit={sendTestMessage} className="space-y-3">
                        <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">
                                Telefone (com DDD)
                            </label>
                            <div className="relative">
                                <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                                <input
                                    type="text"
                                    value={testPhone}
                                    onChange={(e) => setTestPhone(e.target.value.replace(/\D/g, ''))}
                                    placeholder="11999999999"
                                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#449879] focus:border-transparent text-sm"
                                    disabled={sending || !isConnected}
                                    maxLength={13}
                                />
                            </div>
                            <p className="text-xs text-gray-400 mt-1">Apenas números, sem espaços ou caracteres especiais</p>
                        </div>
                        <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">
                                Mensagem
                            </label>
                            <textarea
                                value={testMessage}
                                onChange={(e) => setTestMessage(e.target.value)}
                                placeholder="Digite a mensagem de teste..."
                                rows="2"
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#449879] focus:border-transparent text-sm resize-none"
                                disabled={sending || !isConnected}
                            />
                        </div>
                        <button
                            type="submit"
                            disabled={sending || !isConnected}
                            className={`w-full py-2.5 rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-2 ${
                                isConnected
                                    ? 'bg-[#25D366] text-white hover:bg-[#1DA851]'
                                    : 'bg-gray-200 text-gray-500 cursor-not-allowed'
                            }`}
                        >
                            {sending ? (
                                <>
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                    Enviando...
                                </>
                            ) : (
                                <>
                                    <Send className="w-4 h-4" />
                                    Enviar mensagem
                                </>
                            )}
                        </button>
                    </form>
                </div>

                {/* Ayuda */}
                <div className="mt-4 p-3 bg-blue-50 border border-blue-100 rounded-lg">
                    <div className="flex items-start gap-2">
                        <AlertCircle className="w-4 h-4 text-blue-500 mt-0.5 shrink-0" />
                        <div>
                            <p className="text-xs text-blue-700">
                                <strong>Dica:</strong> Para testar, use seu próprio número de WhatsApp. 
                                O bot enviará a mensagem do número do Brechó Vizinho para seu WhatsApp pessoal.
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default WhatsAppBotPanel;